import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Observable, of, from } from 'rxjs';
import { map, catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { WrxlRulesService } from '../../services/wxrl-rules.service';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { WXRL_DIRECT_CONDITION, isDirectCondition } from '../../utils/wxrl-direct';

@Component({
  selector: 'afoone-wxrl-header-drawer',
  templateUrl: './wxrl-header-drawer.component.html',
  styleUrls: ['./wxrl-header-drawer.component.css'],
})
export class WxrlHeaderDrawerComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() headerData: any = null;
  @Input() conditions: Array<{ condRule: string; kommentar: string }> = [];
  @Input() isNewRule: boolean = false;
  /**
   * Si true, Cancel no cierra el sidebar (el padre confirma abandono; si rechaza, el drawer sigue abierto).
   * Solo modo primer acceso automático al crear regla.
   */
  @Input() cancelKeepsDrawerOpen: boolean = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();
  /** Padre debe vaciar RULESCONDITION al aceptar borrado por modo DIRECT (#705). */
  @Output() clearConditions = new EventEmitter<void>();

  public headerForm: FormGroup;
  /** Evita renderizar p-inputNumber antes de patchValue (NG0100 ruleNr '' → 41). */
  public headerFormReady = false;

  private hasPopulated: boolean = false;
  private populatingHeader = false;

  /**
   * Overlay modal del p-sidebar. PrimeNG solo retira la máscara en `hide()` si `modal` sigue true
   * al terminar la animación de cierre; no forzar false al pasar `visible` a false (#698).
   */
  firstAccessModalOverlay = false;

  /** Misma referencia nueva cada CD desde `getConditionsForFormulaEditor()`; solo revalidar si el contenido cambia. */
  private lastHeaderConditionsFingerprint: string | undefined;

  /**
   * Fórmula obligatoria salvo DIRECT; si aún no hay condiciones en la regla, la fórmula vacía es válida
   * (cabecera antes del árbol — primera entrada #698).
   */
  private readonly headerFormulaOrDirectValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const direct = group.get('directExecution')?.value === true;
    if (direct) {
      return null;
    }
    const condition = String(group.get('condition')?.value ?? '').trim();
    if (!condition && (this.conditions?.length ?? 0) === 0) {
      return null;
    }
    if (!condition) {
      return { formulaRequiredUnlessDirect: true };
    }
    return null;
  };

  constructor(
    private _fb: FormBuilder,
    private _wxrlRulesService: WrxlRulesService,
    private _confirmationService: ConfirmationService,
    private _translate: TranslateService,
    private _cdr: ChangeDetectorRef
  ) {
    this.headerForm = this._fb.group(
      {
        ruleNr: [{ value: null, disabled: true }],
        ruleName: ['', Validators.required],
        ruleKrz: ['', [Validators.required, Validators.maxLength(10)]],
        kommentar: [''],
        condition: [''],
        negRule: [false],
        directExecution: [false],
      },
      { validators: [this.headerFormulaOrDirectValidator] }
    );

    this.headerForm.get('directExecution')?.valueChanges.subscribe((direct) => {
      if (this.populatingHeader) {
        return;
      }
      if (direct) {
        const n = this.conditions?.length ?? 0;
        if (n > 0) {
          this._confirmationService.confirm({
            message: this._translate.instant(
              'WXRL.RULE.FORM.NO_CONDITIONS_RULE_CONFIRM'
            ),
            header: this._translate.instant('WXRL.EDIT_MODE.CONFIRM_HEADER'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
              this.clearConditions.emit();
              queueMicrotask(() => {
                this.headerForm.patchValue({ condition: '' }, { emitEvent: false });
                this.scheduleHeaderFormRevalidate();
              });
            },
            reject: () => {
              this.headerForm
                .get('directExecution')
                ?.patchValue(false, { emitEvent: false });
            },
          });
        } else {
          this.headerForm.patchValue({ condition: '' }, { emitEvent: false });
          this.scheduleHeaderFormRevalidate();
        }
      } else {
        this.scheduleHeaderFormRevalidate();
      }
    });

    this.headerForm.get('condition')?.valueChanges.subscribe(() => {
      this.scheduleHeaderFormRevalidate();
    });
  }

  /** Evita NG0100 al revalidar el grupo en el mismo ciclo que el checkbox (#705). */
  private scheduleHeaderFormRevalidate(): void {
    queueMicrotask(() => {
      this.headerForm.updateValueAndValidity({ emitEvent: false });
      this._cdr.markForCheck();
    });
  }

  private newRuleNrAsyncValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    if (control.value == null || control.value === '') {
      return of(null);
    }
    const ruleNr = Number(control.value);
    if (!Number.isInteger(ruleNr) || ruleNr < 1) {
      return of(null);
    }
    return of(control.value).pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((nr) =>
        from(this._wxrlRulesService.ruleExists(Number(nr))).pipe(
          map((exists) => (exists ? { ruleExists: true } : null)),
          catchError(() => of(null))
        )
      )
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.syncFirstAccessModalOverlay();
    if (changes['conditions'] && !changes['visible'] && !changes['headerData']) {
      const fp = (this.conditions ?? [])
        .map((c) => `${String(c.condRule ?? '')}\u0001${String(c.kommentar ?? '')}`)
        .join('|');
      if (fp !== this.lastHeaderConditionsFingerprint) {
        this.lastHeaderConditionsFingerprint = fp;
        this.scheduleHeaderFormRevalidate();
      }
      return;
    }

    const openWithData =
      (changes['visible'] && this.visible && this.headerData) ||
      (changes['headerData'] && this.visible && this.headerData && !this.hasPopulated);
    if (openWithData) {
      this.hasPopulated = false;
      this.headerFormReady = false;
      setTimeout(() => {
        this.populateForm();
        this.headerFormReady = true;
        this._cdr.markForCheck();
      }, 0);
    } else if (changes['visible'] && !this.visible) {
      this.headerFormReady = false;
      this.headerForm.reset();
      this.hasPopulated = false;
      this.lastHeaderConditionsFingerprint = undefined;
    }
  }

  private syncFirstAccessModalOverlay(): void {
    if (this.visible && this.cancelKeepsDrawerOpen) {
      this.firstAccessModalOverlay = true;
      return;
    }
    if (this.visible && !this.cancelKeepsDrawerOpen) {
      this.firstAccessModalOverlay = false;
      return;
    }
  }

  /** Tras animación de cierre: bajar modal y permitir que PrimeNG destruya la máscara. */
  onPrimeSidebarHide(): void {
    this.firstAccessModalOverlay = false;
    this._cdr.markForCheck();
  }

  private populateForm(): void {
    if (!this.headerData) return;

    this.populatingHeader = true;
    try {
      this.headerForm.patchValue(
        {
          ruleNr: this.headerData.ruleNr ?? null,
          ruleName: this.headerData.ruleName || '',
          ruleKrz: this.headerData.ruleKrz || '',
          kommentar: this.headerData.kommentar || '',
          condition: isDirectCondition(this.headerData.condition)
            ? ''
            : this.headerData.condition || '',
          negRule: this.headerData.negRule || false,
          directExecution:
            !!this.headerData.directExecution ||
            isDirectCondition(this.headerData.condition),
        },
        { emitEvent: false }
      );
    } finally {
      this.populatingHeader = false;
      queueMicrotask(() => {
        this.headerForm.updateValueAndValidity({ emitEvent: false });
        this._cdr.markForCheck();
      });
    }

    const ruleNrCtrl = this.headerForm.get('ruleNr');
    if (ruleNrCtrl) {
      if (this.isNewRule) {
        ruleNrCtrl.enable({ emitEvent: false });
        ruleNrCtrl.setValidators([Validators.required, Validators.min(1)]);
        ruleNrCtrl.setAsyncValidators([this.newRuleNrAsyncValidator.bind(this)]);
      } else {
        ruleNrCtrl.clearAsyncValidators();
        ruleNrCtrl.clearValidators();
        ruleNrCtrl.disable({ emitEvent: false });
      }
      ruleNrCtrl.updateValueAndValidity({ emitEvent: false });
    }

    this.hasPopulated = true;
  }

  onSave(): void {
    if (this.headerForm.invalid) {
      Object.keys(this.headerForm.controls).forEach((key) => {
        this.headerForm.get(key)?.markAsTouched();
      });
      return;
    }
    const raw = this.headerForm.getRawValue();
    this.save.emit({
      ruleNr: raw.ruleNr,
      ruleName: raw.ruleName,
      ruleKrz: raw.ruleKrz,
      kommentar: raw.kommentar,
      negRule: raw.negRule,
      directExecution: !!raw.directExecution,
      condition: raw.directExecution
        ? WXRL_DIRECT_CONDITION
        : String(raw.condition ?? '').trim(),
    });
  }

  onCancel(): void {
    if (this.cancelKeepsDrawerOpen) {
      this.cancel.emit();
      return;
    }
    this.visibleChange.emit(false);
    this.cancel.emit();
  }

  /**
   * Con `cancelKeepsDrawerOpen`, p-sidebar no debe cerrar con Escape (saltaría el flujo del padre).
   * Reenviamos Escape a la misma ruta que el botón Cancelar.
   */
  @HostListener('document:keydown.escape', ['$event'])
  onDocumentEscape(ev: Event): void {
    if (!this.visible || !this.cancelKeepsDrawerOpen) {
      return;
    }
    ev.preventDefault();
    ev.stopPropagation();
    this.onCancel();
  }

  /**
   * El cierre del p-sidebar (X, etc.) no debe propagar `visible=false` al padre si estamos en
   * primer acceso: mismo flujo que Cancelar (confirmación de abandono).
   */
  onSidebarVisibleChange(value: boolean): void {
    if (!value && this.cancelKeepsDrawerOpen) {
      this.cancel.emit();
      return;
    }
    this.visibleChange.emit(value);
  }
}
